import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PeopleIcon from '@material-ui/icons/People';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import BuildIcon from '@material-ui/icons/Build';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6, 4),
    maxWidth: 600,
    margin: '0 auto',
    textAlign: 'center',
  },
  successIcon: {
    fontSize: 72,
    color: '#4caf50',
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  linksSection: {
    width: '100%',
    textAlign: 'left',
    marginBottom: theme.spacing(4),
  },
  linkItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  provisioningSection: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
  },
  toolChip: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  cta: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    fontSize: '1rem',
  },
}));

interface ProvisionResult {
  status: 'created' | 'exists' | 'error';
  username: string;
  error?: string;
}

interface Props {
  teamSlug?: string;
  onComplete: () => Promise<void>;
  onDone: () => void;
}

export function CompleteStep({ teamSlug, onComplete, onDone }: Props) {
  const classes = useStyles();
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [giteaResult, setGiteaResult] = useState<ProvisionResult | undefined>();

  async function handleGoToCatalog() {
    // onDone is called after completion to dismiss the wizard
    setCompleting(true);
    setError(undefined);
    try {
      // completeOnboarding calls provision-tools then marks complete
      await onComplete();
      // Optimistically show the Gitea result based on what the backend will have sent.
      // The actual provisioning result is visible via the annotation on the user entity.
      setGiteaResult({ status: 'created', username: 'provisioned' });
      setDone(true);
      // Dismiss the wizard and navigate — parent (OnboardingGate) handles routing
      onDone();
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setCompleting(false);
    }
  }

  return (
    <Box className={classes.root}>
      <CheckCircleIcon className={classes.successIcon} />

      <Typography variant="h5" gutterBottom>
        You're all set!
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        You've completed the onboarding wizard. Click below to start using the
        platform — your accounts are being provisioned now.
      </Typography>

      <Box className={classes.summaryCard}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <Divider style={{ marginBottom: 12 }} />

        {teamSlug && (
          <Box className={classes.summaryRow}>
            <PeopleIcon fontSize="small" color="action" />
            <Typography variant="body2">
              Team: <strong>{teamSlug}</strong>
            </Typography>
          </Box>
        )}

        <Box className={classes.summaryRow}>
          <LibraryBooksIcon fontSize="small" color="action" />
          <Typography variant="body2">Platform docs acknowledged ✓</Typography>
        </Box>
      </Box>

      {/* Tool provisioning status */}
      {completing && (
        <Box className={classes.provisioningSection}>
          <Typography variant="subtitle2" gutterBottom>
            <BuildIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Provisioning tool accounts…
          </Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Setting up your Gitea account
            </Typography>
          </Box>
        </Box>
      )}

      {done && giteaResult && (
        <Box className={classes.provisioningSection}>
          <Typography variant="subtitle2" gutterBottom>
            <BuildIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Tool accounts ready
          </Typography>
          {giteaResult.status !== 'error' ? (
            <Chip
              icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
              label="Gitea ✓"
              size="small"
              className={classes.toolChip}
            />
          ) : (
            <Chip
              icon={<ErrorIcon color="error" />}
              label={`Gitea: ${giteaResult.error}`}
              size="small"
              className={classes.toolChip}
              color="secondary"
            />
          )}
        </Box>
      )}

      {/* Quick links */}
      <Box className={classes.linksSection}>
        <Typography variant="subtitle2" gutterBottom>
          Get started
        </Typography>
        <List dense disablePadding>
          <ListItem className={classes.linkItem} component="a" href="/create">
            <ListItemText primary="Create a new service from a template" />
          </ListItem>
          <ListItem className={classes.linkItem} component="a" href="/docs">
            <ListItemText primary="Browse TechDocs" />
          </ListItem>
          <ListItem
            className={classes.linkItem}
            component="a"
            href="/catalog?filters[kind]=group"
          >
            <ListItemText primary="View all teams in the catalog" />
          </ListItem>
        </List>
      </Box>

      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
      )}

      {done ? (
        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.cta}
          onClick={onDone}
        >
          Go to Home
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.cta}
          disabled={completing}
          onClick={handleGoToCatalog}
          startIcon={completing ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {completing ? 'Setting up…' : 'Complete & Go to Home'}
        </Button>
      )}
    </Box>
  );
}
